import { useAuth } from "@/src/contexts/auth.context";
import {
  ApiCategoryWithCount,
  BlocksApiArticle,
  DeleteArticleOptions,
} from "@/src/models/wiki.models";
import {
  WIKI_ARTICLE_QUERY_KEY,
  WIKI_ARTICLES_BY_CATEGORY_QUERY_KEY,
  WIKI_CATEGORIES_QUERY_KEY,
  WIKI_DRAFT_ARTICLES_QUERY_KEY,
  WIKI_SAVED_ARTICLES_QUERY_KEY,
} from "@/src/query-keys/wiki.keys";
import { useGetCategoriesQuery } from "@/src/services/api/category/category.hooks";
import gqlClient from "@/src/services/api/graphql.client";
import {
  useGetUserProfileQuery,
  useToggleSaved,
} from "@/src/services/profile/profile.hooks";
import { DELETE_ARTICLE, GET_ARTICLES } from "@/src/services/wiki/wiki.query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const DEFAULT_AUTHOR_AVATAR = require("@/src/assets/images/profile.png");

const request = async (query: string, variables?: object) => {
  const client = await gqlClient();
  return client.request(query, variables);
};

const fetchBlocksArticles = async (
  filter: object,
  pageSize: number,
  pageNo = 1,
): Promise<{ items: BlocksApiArticle[]; totalCount: number }> => {
  const data: any = await request(GET_ARTICLES, {
    filter: JSON.stringify(filter),
    pageSize,
    pageNo,
  });
  return {
    items: data?.getwikis?.items ?? [],
    totalCount: data?.getwikis?.totalCount ?? 0,
  };
};

const buildFilter = (
  extra: object,
  search?: string,
  isDraft = false,
): object => {
  const filter: Record<string, unknown> = { isDraft, ...extra };
  if (search?.trim()) {
    filter.Title = { $regex: search.trim(), $options: "i" };
  }
  return filter;
};

export const resolveAuthorName = (article: BlocksApiArticle): string => {
  const { authorSnapshot } = article;
  if (!authorSnapshot) return article.CreatedBy ?? "";
  const full =
    `${authorSnapshot.firstName ?? ""} ${authorSnapshot.lastName ?? ""}`.trim();
  return (full || article.CreatedBy) ?? "";
};

const normalizeCategoryId = (
  ids: string | string[] | undefined,
): string | null => {
  if (!ids) return null;
  if (Array.isArray(ids)) return ids[0] ?? null;
  return ids;
};

export const useWikiArticle = (articleId?: string, enabled = true) =>
  useQuery({
    queryKey: [...WIKI_ARTICLE_QUERY_KEY, articleId ?? ""],
    queryFn: () =>
      fetchBlocksArticles({ ItemId: articleId, isDraft: false }, 1).then(
        (result) => result.items[0] ?? null,
      ),
    enabled: enabled && !!articleId,
  });

export const useWikiCategoriesQuery = () => {
  const { data: categoriesResult } = useGetCategoriesQuery({
    pageNo: 1,
    search: "",
    tag: "wiki",
    pageSize: 100,
  });
  const categories = categoriesResult?.items ?? [];

  return useQuery({
    queryKey: WIKI_CATEGORIES_QUERY_KEY,
    queryFn: async (): Promise<ApiCategoryWithCount[]> => {
      const { items: allArticles } = await fetchBlocksArticles(
        { isDraft: false },
        1000,
      );

      const countMap = new Map<string, ApiCategoryWithCount>();

      for (const article of allArticles) {
        const categoryId = normalizeCategoryId(article.categoryIds);
        if (!categoryId) continue;
        const category = categories.find((c) => c.ItemId === categoryId);
        if (!category?.categoryName) continue;
        if (countMap.has(categoryId)) {
          countMap.get(categoryId)!.articleCount += 1;
        } else {
          countMap.set(categoryId, {
            categoryID: categoryId,
            name: category.categoryName,
            articleCount: 1,
          });
        }
      }

      return Array.from(countMap.values());
    },
    enabled: categories.length > 0,
    staleTime: 5 * 60 * 1000,
  });
};

export const useWikiArticlesByCategory = (
  categoryId: string,
  search?: string,
) =>
  useQuery({
    queryKey: [
      ...WIKI_ARTICLES_BY_CATEGORY_QUERY_KEY,
      categoryId,
      search ?? "",
    ],
    queryFn: () =>
      fetchBlocksArticles(
        buildFilter({ categoryIds: categoryId }, search, false),
        100,
      ).then((result) => result.items),
    enabled: !!categoryId,
    staleTime: 5 * 60 * 1000,
  });

export const useWikiSaveState = (search?: string) => {
  const { user } = useAuth();
  const userId = user?.itemId ?? "";
  const { data: profile } = useGetUserProfileQuery(userId);
  const savedArticleIds = Array.isArray(profile?.wikiSaved)
    ? profile.wikiSaved
    : [];

  const { data: allSavedArticles = [], isLoading: isLoadingSavedArticles } =
    useQuery({
      queryKey: [...WIKI_SAVED_ARTICLES_QUERY_KEY, userId, savedArticleIds],
      queryFn: async () => {
        if (!savedArticleIds.length) return [];
        const results = await Promise.all(
          savedArticleIds.map((id) =>
            fetchBlocksArticles({ ItemId: id, isDraft: false }, 1).then(
              (res) => res.items[0],
            ),
          ),
        );
        return results.filter(Boolean) as BlocksApiArticle[];
      },
      enabled: !!userId && !!profile,
    });

  const savedArticles = search?.trim()
    ? allSavedArticles.filter((article) =>
        article.Title?.toLowerCase().includes(search.toLowerCase()),
      )
    : allSavedArticles;

  return {
    savedArticles,
    isSaved: (id: string) => savedArticleIds.includes(id),
    isLoadingSavedArticles,
  };
};

export const useWikiDraftArticles = (search?: string) => {
  const { user } = useAuth();
  const userId = user?.itemId ?? "";
  const { data: profile } = useGetUserProfileQuery(userId);
  const draftArticleIds = Array.isArray(profile?.wikiDraftIds)
    ? profile.wikiDraftIds
    : [];

  return useQuery({
    queryKey: [
      ...WIKI_DRAFT_ARTICLES_QUERY_KEY,
      userId,
      draftArticleIds,
      search ?? "",
    ],
    queryFn: () => {
      if (draftArticleIds.length === 0) return [];
      return fetchBlocksArticles(
        buildFilter({ ItemId: { $in: [...draftArticleIds] } }, search, true),
        draftArticleIds.length,
      ).then((result) => result.items);
    },
    enabled: !!userId && draftArticleIds.length > 0,
  });
};

export const useToggleWikiSaved = (userId: string) => {
  const queryClient = useQueryClient();
  const base = useToggleSaved(userId, "wiki");
  const optimisticallyRemoveFromSaved = (articleId: string) => {
    const snapshots = queryClient.getQueriesData<BlocksApiArticle[]>({
      queryKey: WIKI_SAVED_ARTICLES_QUERY_KEY,
    });

    const wasSaved = snapshots.some(([, articles]) =>
      (articles ?? []).some((article) => article.ItemId === articleId),
    );

    if (wasSaved) {
      for (const [key, articles] of snapshots) {
        if (!articles) continue;
        const filtered = articles.filter(
          (article) => article.ItemId !== articleId,
        );
        queryClient.setQueryData(key, filtered);
        const ids = (key as unknown[])[key.length - 1];
        if (Array.isArray(ids) && ids.includes(articleId)) {
          const nextKey = [
            ...key.slice(0, -1),
            ids.filter((id) => id !== articleId),
          ];
          queryClient.setQueryData(nextKey, filtered);
        }
      }
    }

    return { savedSnapshots: snapshots, wasSaved };
  };

  const rollbackSaved = (snapshots: [readonly unknown[], unknown][]) => {
    for (const [key, articles] of snapshots) {
      queryClient.setQueryData(key, articles);
    }
  };

  return {
    ...base,
    mutate: (articleId: string) => {
      const { savedSnapshots, wasSaved } =
        optimisticallyRemoveFromSaved(articleId);

      base.mutate(articleId, {
        onError: () => {
          rollbackSaved(savedSnapshots);
        },
        onSuccess: () => {
          if (!wasSaved) {
            queryClient.invalidateQueries({
              queryKey: WIKI_SAVED_ARTICLES_QUERY_KEY,
            });
          }
        },
      });
    },
    mutateAsync: async (articleId: string) => {
      const { savedSnapshots, wasSaved } =
        optimisticallyRemoveFromSaved(articleId);

      return base.mutateAsync(articleId, {
        onError: () => {
          rollbackSaved(savedSnapshots);
        },
        onSuccess: () => {
          if (!wasSaved) {
            queryClient.invalidateQueries({
              queryKey: WIKI_SAVED_ARTICLES_QUERY_KEY,
            });
          }
        },
      });
    },
  };
};

export const useWikiDeleteArticle = ({
  categoryId,
  onSuccess,
}: DeleteArticleOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (itemId: string) => {
      await request(DELETE_ARTICLE, {
        filter: JSON.stringify({ ItemId: itemId }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WIKI_ARTICLE_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: WIKI_CATEGORIES_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: WIKI_SAVED_ARTICLES_QUERY_KEY,
      });
      queryClient.invalidateQueries({
        queryKey: WIKI_DRAFT_ARTICLES_QUERY_KEY,
      });
      queryClient.invalidateQueries({
        queryKey: WIKI_ARTICLES_BY_CATEGORY_QUERY_KEY,
      });
      if (categoryId) {
        queryClient.invalidateQueries({
          queryKey: [...WIKI_ARTICLES_BY_CATEGORY_QUERY_KEY, categoryId],
        });
      }
      onSuccess?.();
    },
  });
};
